/**
 * @component ry-progress
 * @category  feedback
 * @summary   Linear progress bar (Fomantic UI `.ui.progress` equivalent). Driven by `value` over `max`; renders a fill bar that animates to the new width on attribute change. Supports `indeterminate` (no defined value), variant colors, an optional label, and `ry:change` events on every value update.
 *
 * Reference: Fomantic UI's progress module. ry-progress models linear bars
 * (horizontal). Circular / spinner-style progress is intentionally not
 * modelled here — use `<ry-loader>` (when shipped) for indeterminate spinners.
 *
 * @attribute value          number?   default=0      Current value. Clamped to [0, max] at read time. Out-of-range values clamp visually but the attribute is preserved verbatim.
 * @attribute max            number?   default=100    Maximum value. Defaults to 100 so `value="42"` reads as 42% with no max set.
 * @attribute variant        'info' | 'success' | 'warning' | 'error'?   Visual variant — pure CSS hook on the host.
 * @attribute indeterminate  boolean   default=false  When present, the bar shows an indeterminate animation and ignores `value` / `max`. Use for "we don't know how long this takes" affordances.
 * @attribute label          string?                  Optional caption rendered below the bar. Common values are progress percentages, status strings, or the actual file name being uploaded.
 * @attribute size           'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge'?   default="medium"   Bar height. Matches Fomantic UI's progress sizes (tiny 4px → huge 28px).
 *
 * @property  value          number    Live numeric value clamped to [0, max]. Setting writes the `value` attribute.
 * @property  max            number    Live numeric max. Setting writes the `max` attribute.
 * @property  percent        number    Computed read-only — `(value / max) * 100`, clamped to [0, 100]. Returns 0 when indeterminate.
 *
 * @method    set(value)        Set the value (writes attribute, fires `ry:change`).
 * @method    increment(by=1)   Add `by` to the current value, clamped to [0, max]. Useful for "+1 step" patterns.
 *
 * @event ry:change
 *        detail: { value: number, max: number, percent: number }
 *        fires:  on every value / max attribute change (whether driven by user setAttribute, `set()`, or `increment()`). Does NOT fire while indeterminate.
 *
 * @state     `data-ry-state`: `"indeterminate"` when the attribute is present; otherwise unset.
 *
 * @content   None — `<ry-progress>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom
 *   ry-progress[value][max][variant?][indeterminate?][label?]   role="progressbar" aria-valuenow=value aria-valuemax=max aria-valuemin="0"
 *     > div.ry-progress__track   [data-ry-target="track"]
 *       > div.ry-progress__bar   [data-ry-target="bar"]   style="width: {percent}%"
 *     > div.ry-progress__label   [data-ry-target="label"]   (only when [label])
 *         > {label text}
 *
 * @gotcha  `aria-valuenow` is omitted when `[indeterminate]`. Screen readers announce indeterminate progress generically without a number. This is the WAI-ARIA-correct behavior.
 *
 * @gotcha  `value > max` is clamped visually to 100% but the attribute keeps the literal value. Reading `el.value` returns the clamped value; reading `el.getAttribute('value')` returns the raw text. Consumers that want a strict ceiling should clamp before setting.
 *
 * @gotcha  Negative values clamp to 0 visually. The attribute is preserved verbatim. `el.value` returns 0.
 *
 * @gotcha  Setting `value` to a non-numeric string (`"foo"`) — `el.value` returns 0, the bar shows 0%, and `ry:change` fires with value=0. No warning. Consumers that need validation should check before setting.
 *
 * @gotcha  `[indeterminate]` overrides `value` / `max` — the bar animates regardless of those attributes. `ry:change` does NOT fire while indeterminate; if you toggle indeterminate off, the next value change emits as usual.
 *
 * @example basic
 * ```html
 * <ry-progress value="42"></ry-progress>
 * ```
 *
 * @example with-label
 * ```html
 * <ry-progress value="73" label="Uploading…"></ry-progress>
 * ```
 *
 * @example variants
 * ```html
 * <ry-progress value="40" variant="info" label="Working"></ry-progress>
 * <ry-progress value="100" variant="success" label="Done"></ry-progress>
 * <ry-progress value="60" variant="warning" label="Almost full"></ry-progress>
 * <ry-progress value="20" variant="error" label="Failing checks"></ry-progress>
 * ```
 *
 * @example sizes
 * ```html
 * <div style="display: flex; flex-direction: column; gap: 1.25rem;">
 *   <ry-progress value="50" size="tiny" label="tiny"></ry-progress>
 *   <ry-progress value="50" size="small" label="small"></ry-progress>
 *   <ry-progress value="50" size="medium" label="medium (default)"></ry-progress>
 *   <ry-progress value="50" size="large" label="large"></ry-progress>
 *   <ry-progress value="50" size="big" label="big"></ry-progress>
 *   <ry-progress value="50" size="huge" label="huge"></ry-progress>
 * </div>
 * ```
 *
 * @example custom-max
 * ```html
 * <!-- Step 3 of 5 -->
 * <ry-progress value="3" max="5" label="Step 3 of 5"></ry-progress>
 * ```
 *
 * @example indeterminate
 * ```html
 * <ry-progress indeterminate label="Connecting…"></ry-progress>
 * ```
 *
 * @tutorial value-and-max-drive-percent
 * ```js
 * const el = mount('<ry-progress value="42"></ry-progress>');
 * await expectEqual(el.value, 42);
 * await expectEqual(el.max, 100, 'default max is 100');
 * await expectEqual(el.percent, 42, 'percent = value/max * 100');
 * const bar = el.querySelector('[data-ry-target="bar"]');
 * await expectEqual(bar.style.width, '42%', 'bar width reflects percent');
 * ```
 *
 * @tutorial custom-max-changes-percent
 * ```js
 * // Wrong assumption: percent always equals value. With max=5 and value=3,
 * // percent is 60, not 3.
 * const el = mount('<ry-progress value="3" max="5"></ry-progress>');
 * await expectEqual(el.percent, 60, 'percent reflects value/max ratio');
 * ```
 *
 * @tutorial out-of-range-clamps-visually
 * ```js
 * // Wrong assumption: setting value=200 with max=100 errors or sets 200%.
 * // It clamps visually to 100% but the attribute preserves the raw text.
 * const el = mount('<ry-progress value="200" max="100"></ry-progress>');
 * await expectEqual(el.percent, 100, 'clamped to 100');
 * await expectEqual(el.getAttribute('value'), '200', 'raw attribute preserved');
 * const negative = mount('<ry-progress value="-5"></ry-progress>');
 * await expectEqual(negative.percent, 0, 'negative clamps to 0');
 * ```
 *
 * @tutorial set-method-fires-ry-change
 * ```js
 * const el = mount('<ry-progress value="0"></ry-progress>');
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail));
 * el.set(50);
 * await expectEqual(el.value, 50);
 * await expectEqual(fired.length, 1);
 * await expectEqual(fired[0].value, 50);
 * await expectEqual(fired[0].max, 100);
 * await expectEqual(fired[0].percent, 50);
 * ```
 *
 * @tutorial increment-clamps-to-max
 * ```js
 * const el = mount('<ry-progress value="98" max="100"></ry-progress>');
 * el.increment(5);
 * await expectEqual(el.value, 100, 'increment clamps to max');
 * el.increment(-200);
 * await expectEqual(el.value, 0, 'negative increment clamps to 0');
 * ```
 *
 * @tutorial indeterminate-suppresses-ry-change
 * ```js
 * // Wrong assumption: ry:change fires regardless of state. While
 * // [indeterminate] is on, value changes do NOT emit — the bar isn't
 * // actually showing the value anyway.
 * const el = mount('<ry-progress indeterminate value="0"></ry-progress>');
 * const fired = [];
 * el.addEventListener('ry:change', () => fired.push(1));
 * el.set(50);
 * await expectEqual(fired.length, 0, 'no ry:change while indeterminate');
 * el.removeAttribute('indeterminate');
 * el.set(75);
 * await expectEqual(fired.length, 1, 'fires after indeterminate is cleared');
 * ```
 *
 * @tutorial aria-attributes-mirror-state
 * ```js
 * const el = mount('<ry-progress value="30" max="50"></ry-progress>');
 * await expectAttr(el, 'role', 'progressbar');
 * await expectAttr(el, 'aria-valuenow', '30');
 * await expectAttr(el, 'aria-valuemax', '50');
 * await expectAttr(el, 'aria-valuemin', '0');
 * el.set(45);
 * await expectAttr(el, 'aria-valuenow', '45', 'aria-valuenow tracks value');
 * ```
 *
 * @tutorial indeterminate-removes-aria-valuenow
 * ```js
 * // a11y contract: aria-valuenow is undefined for indeterminate progress.
 * const el = mount('<ry-progress value="42" indeterminate></ry-progress>');
 * await expectEqual(el.hasAttribute('aria-valuenow'), false, 'no aria-valuenow when indeterminate');
 * await expectAttr(el, 'data-ry-state', 'indeterminate');
 * ```
 *
 * @tutorial label-attribute-renders-slot
 * ```js
 * const el = mount('<ry-progress value="50" label="Uploading"></ry-progress>');
 * const lbl = el.querySelector('[data-ry-target="label"]');
 * await expectEqual(!!lbl, true);
 * await expectEqual(lbl.textContent, 'Uploading');
 * ```
 *
 * @tutorial no-label-slot-without-attribute
 * ```js
 * const el = mount('<ry-progress value="50"></ry-progress>');
 * await expectEqual(el.querySelector('[data-ry-target="label"]'), null, 'no label slot without attribute');
 * ```
 *
 * @see ry-toast
 * @see ry-alert
 */

import { RyElement } from '../core/ry-element.js';

interface ProgressChangeDetail {
  value: number;
  max: number;
  percent: number;
}

function clamp(n: number, lo: number, hi: number): number {
  if (Number.isNaN(n)) return lo;
  return Math.max(lo, Math.min(hi, n));
}

export class RyProgress extends RyElement {
  static get observedAttributes(): string[] {
    return ['value', 'max', 'label', 'indeterminate'];
  }

  setup(): void {
    this.#render();
    this.#applyAria();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.hasAttribute('data-ry-initialized')) return;

    if (name === 'label') {
      this.#renderLabel();
    } else if (name === 'indeterminate') {
      this.#applyAria();
      this.#updateBar();
    } else if (name === 'value' || name === 'max') {
      this.#applyAria();
      this.#updateBar();
      if (!this.hasAttribute('indeterminate')) {
        this.emit<ProgressChangeDetail>('change', {
          value: this.value,
          max: this.max,
          percent: this.percent,
        });
      }
    }
  }

  get value(): number {
    const raw = parseFloat(this.getAttribute('value') ?? '0');
    return clamp(Number.isFinite(raw) ? raw : 0, 0, this.max);
  }

  set value(v: number) {
    this.setAttribute('value', String(v));
  }

  get max(): number {
    const raw = parseFloat(this.getAttribute('max') ?? '100');
    return Number.isFinite(raw) && raw > 0 ? raw : 100;
  }

  set max(v: number) {
    this.setAttribute('max', String(v));
  }

  get percent(): number {
    if (this.hasAttribute('indeterminate')) return 0;
    const m = this.max;
    return m > 0 ? clamp((this.value / m) * 100, 0, 100) : 0;
  }

  set(value: number): void {
    this.setAttribute('value', String(value));
  }

  increment(by = 1): void {
    const next = clamp(this.value + by, 0, this.max);
    this.setAttribute('value', String(next));
  }

  #render(): void {
    if (this.$('[data-ry-target="track"]')) return;
    const labelAttr = this.getAttribute('label');
    const labelMarkup = labelAttr
      ? `<div data-ry-target="label" class="ry-progress__label">${labelAttr}</div>`
      : '';
    this.innerHTML = `
      <div data-ry-target="track" class="ry-progress__track">
        <div data-ry-target="bar" class="ry-progress__bar"></div>
      </div>
      ${labelMarkup}
    `;
    this.#updateBar();
  }

  #updateBar(): void {
    const bar = this.$<HTMLElement>('[data-ry-target="bar"]');
    if (!bar) return;
    if (this.hasAttribute('indeterminate')) {
      bar.style.width = '';
    } else {
      bar.style.width = `${this.percent}%`;
    }
  }

  #renderLabel(): void {
    const labelText = this.getAttribute('label');
    let labelEl = this.$<HTMLElement>('[data-ry-target="label"]');
    if (labelText) {
      if (!labelEl) {
        labelEl = document.createElement('div');
        labelEl.setAttribute('data-ry-target', 'label');
        labelEl.className = 'ry-progress__label';
        this.appendChild(labelEl);
      }
      labelEl.textContent = labelText;
    } else if (labelEl) {
      labelEl.remove();
    }
  }

  #applyAria(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'progressbar');
    this.setAttribute('aria-valuemin', '0');
    if (this.hasAttribute('indeterminate')) {
      this.removeAttribute('aria-valuenow');
      this.dataset.ryState = 'indeterminate';
    } else {
      this.setAttribute('aria-valuenow', String(this.value));
      this.setAttribute('aria-valuemax', String(this.max));
      if (this.dataset.ryState === 'indeterminate') delete this.dataset.ryState;
    }
    if (!this.hasAttribute('indeterminate')) {
      this.setAttribute('aria-valuemax', String(this.max));
    }
  }
}

customElements.define('ry-progress', RyProgress);
